import React from 'react';
import Input from '../input';

interface Country {
  code: string;
  country: string;
  flag: string;
}

interface MobileNumberInputProps {
  countryCode?: string;
  mobile?: string;
  onChange?: (value: any) => void;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  label?: string;
  className?: string;
}

const MobileNumberInput: React.FC<MobileNumberInputProps> = ({
  countryCode = '+91',
  mobile = '',
  onChange,
  error = '',
  required = false,
  disabled = false,
  placeholder = "Enter mobile number",
  label = "Mobile Number",
  className = ''
}) => {
  // Common country codes with flags
  const countryCodes: Country[] = [
    { code: '+1', country: 'US/CA', flag: '🇺🇸' },
    { code: '+44', country: 'UK', flag: '🇬🇧' },
    { code: '+91', country: 'India', flag: '🇮🇳' },
    { code: '+86', country: 'China', flag: '🇨🇳' },
    { code: '+81', country: 'Japan', flag: '🇯🇵' },
    { code: '+49', country: 'Germany', flag: '🇩🇪' },
    { code: '+33', country: 'France', flag: '🇫🇷' },
    { code: '+39', country: 'Italy', flag: '🇮🇹' },
    { code: '+34', country: 'Spain', flag: '🇪🇸' },
    { code: '+7', country: 'Russia', flag: '🇷🇺' },
    { code: '+55', country: 'Brazil', flag: '🇧🇷' },
    { code: '+52', country: 'Mexico', flag: '🇲🇽' },
    { code: '+61', country: 'Australia', flag: '🇦🇺' },
    { code: '+27', country: 'South Africa', flag: '🇿🇦' },
    { code: '+82', country: 'South Korea', flag: '🇰🇷' },
    { code: '+65', country: 'Singapore', flag: '🇸🇬' },
    { code: '+971', country: 'UAE', flag: '🇦🇪' },
    { code: '+966', country: 'Saudi Arabia', flag: '🇸🇦' },
    { code: '+60', country: 'Malaysia', flag: '🇲🇾' },
    { code: '+66', country: 'Thailand', flag: '🇹🇭' },
    { code: '+84', country: 'Vietnam', flag: '🇻🇳' },
    { code: '+62', country: 'Indonesia', flag: '🇮🇩' },
    { code: '+63', country: 'Philippines', flag: '🇵🇭' },
    { code: '+92', country: 'Pakistan', flag: '🇵🇰' },
    { code: '+880', country: 'Bangladesh', flag: '🇧🇩' },
    { code: '+94', country: 'Sri Lanka', flag: '🇱🇰' },
    { code: '+977', country: 'Nepal', flag: '🇳🇵' },
    { code: '+20', country: 'Egypt', flag: '🇪🇬' },
    { code: '+234', country: 'Nigeria', flag: '🇳🇬' },
    { code: '+254', country: 'Kenya', flag: '🇰🇪' },
    { code: '+256', country: 'Uganda', flag: '🇺🇬' },
    { code: '+233', country: 'Ghana', flag: '🇬🇭' },
    { code: '+212', country: 'Morocco', flag: '🇲🇦' },
    { code: '+213', country: 'Algeria', flag: '🇩🇿' },
    { code: '+216', country: 'Tunisia', flag: '🇹🇳' },
    { code: '+218', country: 'Libya', flag: '🇱🇾' },
    { code: '+90', country: 'Turkey', flag: '🇹🇷' },
    { code: '+98', country: 'Iran', flag: '🇮🇷' },
    { code: '+964', country: 'Iraq', flag: '🇮🇶' },
    { code: '+962', country: 'Jordan', flag: '🇯🇴' },
    { code: '+961', country: 'Lebanon', flag: '🇱🇧' },
    { code: '+972', country: 'Israel', flag: '🇮🇱' },
    { code: '+970', country: 'Palestine', flag: '🇵🇸' },
    { code: '+968', country: 'Oman', flag: '🇴🇲' },
    { code: '+965', country: 'Kuwait', flag: '🇰🇼' },
    { code: '+974', country: 'Qatar', flag: '🇶🇦' },
    { code: '+973', country: 'Bahrain', flag: '🇧🇭' },
    { code: '+967', country: 'Yemen', flag: '🇾🇪' },
    { code: '+963', country: 'Syria', flag: '🇸🇾' },
    { code: '+507', country: 'Panama', flag: '🇵🇦' },
    { code: '+506', country: 'Costa Rica', flag: '🇨🇷' },
    { code: '+503', country: 'El Salvador', flag: '🇸🇻' },
    { code: '+502', country: 'Guatemala', flag: '🇬🇹' },
    { code: '+504', country: 'Honduras', flag: '🇭🇳' },
    { code: '+505', country: 'Nicaragua', flag: '🇳🇮' },
    { code: '+51', country: 'Peru', flag: '🇵🇪' },
    { code: '+56', country: 'Chile', flag: '🇨🇱' },
    { code: '+57', country: 'Colombia', flag: '🇨🇴' },
    { code: '+58', country: 'Venezuela', flag: '🇻🇪' },
    { code: '+593', country: 'Ecuador', flag: '🇪🇨' },
    { code: '+594', country: 'French Guiana', flag: '🇬🇫' },
    { code: '+595', country: 'Paraguay', flag: '🇵🇾' },
    { code: '+598', country: 'Uruguay', flag: '🇺🇾' },
    { code: '+591', country: 'Bolivia', flag: '🇧🇴' },
    { code: '+592', country: 'Guyana', flag: '🇬🇾' },
    { code: '+597', country: 'Suriname', flag: '🇸🇷' }
  ];

  const handleMobileChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value;
    // Only allow numeric input
    const numericValue = value.replace(/[^0-9]/g, '');
    onChange?.({
        target: {
            name: 'mobile',
            value: numericValue
        }
        });
  };

  const handleCountryChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    const value = event.target.value;
    // Update country code
    onChange?.({
        target: {
            name: 'countryCode',
            value: value
        }
    });
  };

  const getFullNumber = (): string => {
    if (!mobile) return '';
    return `${countryCode}${mobile}`;
  };

  return (
    <div className={`input-group ${className}`}>
      <div className="d-flex">
        <select 
          className="form-select me-2" 
          style={{ maxWidth: '110px',maxHeight: '55px' }}
          value={countryCode}
          onChange={handleCountryChange}
          disabled={disabled}
          aria-label="Country code"
          
        >
          {countryCodes.map((country: Country) => (
            <option key={country.code} value={country.code}>
              {country.flag} {country.code}
            </option>
          ))}
        </select>
        
        <div className="flex-grow-1">
          <Input
            disabled={disabled}
            type="tel"
            label={label}
            name="mobile"
            error={error}
            value={mobile}
            onChange={handleMobileChange}
            placeholder={placeholder}
            required={required}
            autoComplete="tel"
          />
        </div>
      </div>
      
      {/* Display full number preview */}
      {mobile && (
        <small className="text-muted mt-1 d-block">
          Complete number: {getFullNumber()}
        </small>
      )}
    </div>
  );
};

export default MobileNumberInput;